import { Box, Grid } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { CompanyActiveState, EnumHelper } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { IListFilterItem } from "../../../models/common/listFilterItem";
import { infiniteListPageSize } from "../../../models/common/webConsts";
import { ICompanyListItem } from "../../../models/company/companyListItem";
import { ICompanyListItems } from "../../../models/company/companyListItems";
import * as companyService from "../../../models/services/companyService";
import { ISessionState } from "../../../models/session/sessionState";
import { ActionContainer } from "../../framework/ActionContainer";
import { AddButton } from "../../framework/Button/AddButton";
import { SettingsButton } from "../../framework/Button/SettingsButton";
import { GridRow } from "../../framework/Grid/GridRow";
import { IListUsingComponentState, List } from "../../framework/List/List";
import { ListActionButtons } from "../../framework/List/ListActionButtons";
import { ListCardField } from "../../framework/List/ListCardField";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { ListDetailsButton } from "../../framework/List/ListDetailsButton";
import { ListLinkValue } from "../../framework/List/ListLinkValue";
import { ListSearchContainer } from "../../framework/List/ListSearchContainer";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { ListToggleButtons } from "../../framework/List/ListToggleButtons";
import { PageTitle } from "../../framework/Page/PageTitle";
import { ProgressBar } from "ui/Scripts/source/components/framework/ProgressBar/ProgressBar";
import { CompanyEditViewMode } from "../CompanyEdit";
import { CompanyEditDialog } from "../CompanyEditDialog";

export interface ICompanyListProp {
    savedFilter: IListFilterItem;
    sessionState: ISessionState;
    disableGlobalLoading?: boolean;
    preloaded?: boolean;
    waitingPreloadedData?: boolean;
    list?: ICompanyListItems;
    listFilterIsSet: boolean;
    companyGroupIds?: string[];
    activityState?: string;
    dataId?: string;
    startDateStr?: string;
    endDateStr?: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    reportTabs: React.ReactNode;
    onRefreshSessionState: () => void;
    onSetSortData: (sortColumn: string, sortOrderIsAsc: boolean) => void;
    onOpenFiltersDialog: () => void;
    onToggleSettingsDialog: () => void;
}

interface ICompanyListState extends IListUsingComponentState<ICompanyListItem> {
    // List
    intialized: boolean;
    isLoading: boolean;
    waitingPreloadedData: boolean;
    pageSize: number;
    page: number;
    filter: string;
    companyGroupIds: string[];
    activityState: number;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    listModeIsLine: boolean;
    checkedIds: string[];

    // Dialog
    editDialogDefaultMenuItem: CompanyEditViewMode;
}

export class CompanyList extends React.Component<ICompanyListProp, ICompanyListState> {
    constructor(props: ICompanyListProp) {
        super(props);
        this.state = {
            intialized: props.list && !!props.preloaded,
            isLoading: false,
            waitingPreloadedData: !Base.isNullOrUndefined(props.waitingPreloadedData) ? props.waitingPreloadedData : (props.list ? false : props.preloaded),
            page: props.list ? props.list.page : 1,
            hasMore: props.list ? props.list.hasMore : true,
            filter: "",
            companyGroupIds: props.companyGroupIds ? props.companyGroupIds.filter(i => i !== Base.emptyGuid) : [],
            activityState: props.activityState ? parseInt(props.activityState, 10) : 0,
            pageSize: props.list ? props.list.pageSize : infiniteListPageSize,
            sortColumn: props.sortColumn,
            sortOrderIsAsc: props.sortOrderIsAsc,
            listModeIsLine: true,
            items: props.list ? props.list.items : [],
            selectedId: null,
            checkedIds: [],
            detailsItem: null,
            showEditDialog: false,
            editDialogId: "",
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogDefaultMenuItem: CompanyEditViewMode.General,
        };
    }

    searchItems = async (pageSize: number, page: number, filter: string, companyGroupIds: string[], activityState: number, sortColumn: string, sortOrderIsAsc: boolean,
        resetItems: boolean, refreshList: boolean, moveNext: boolean = false, setCompanyDialogData: boolean = true) => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => companyService.getCompanyListItems(!refreshList ? pageSize : (page * pageSize), !refreshList ? page : 1, filter, companyGroupIds, activityState, sortColumn, sortOrderIsAsc, null, null),
            false, this.props.disableGlobalLoading, () => this.setState({ isLoading: false }));
        if (!result) return;
        const items = List.getListItems(this.state.items, this.state.selectedId, this.state.checkedIds, result.items, resetItems, refreshList);
        let selectedId = items.selectedId;
        let index = items.items.findIndex(i => i.id === selectedId);
        let detailsItem = index > -1 ? items.items[index] : null;
        if (moveNext && detailsItem && index < items.items.length - 1) {
            index = index + 1;
            detailsItem = items.items[index];
            selectedId = items.items[index].id;
        }
        this.setState({
            intialized: true,
            page: !refreshList ? result.page : page,
            hasMore: result.hasMore,
            filter,
            companyGroupIds,
            activityState,
            sortColumn,
            sortOrderIsAsc,
            items: items.items,
            selectedId,
            checkedIds: items.checkedIds,
            detailsItem,
            editDialogDefaultMenuItem: setCompanyDialogData ? CompanyEditViewMode.General : this.state.editDialogDefaultMenuItem,
            editDialogId: setCompanyDialogData ? (moveNext && detailsItem ? detailsItem.id : "") : this.state.editDialogId,
            editDialogHasPrevious: setCompanyDialogData ? index > 0 : this.state.editDialogHasPrevious,
            editDialogHasNext: setCompanyDialogData ? (index < items.items.length - 1 || result.hasMore) : this.state.editDialogHasNext,
        });
        this.props.onSetSortData(sortColumn, sortOrderIsAsc);
    };

    refreshList = async (setDialogData: boolean = true) => {
        const state = this.state;
        await this.searchItems(state.pageSize, state.page, state.filter, state.companyGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, false, true, false, setDialogData);
    };

    componentDidMount(): void {
        const state = this.state;
        if (this.props.preloaded) return;
        this.searchItems(state.pageSize, state.page, state.filter, state.companyGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, false, false);
    }

    componentDidUpdate(prevProps: ICompanyListProp, prevState: ICompanyListState) {
        const props = this.props;
        const state = this.state;
        if (state.waitingPreloadedData) {
            if (!props.list || (props.dataId ?? "") === (prevProps.dataId ?? "")) return;
            this.setState({
                intialized: true,
                waitingPreloadedData: false,
                page: props.list.page,
                hasMore: props.list.hasMore,
                items: props.list.items,
                pageSize: props.list.pageSize,
                sortColumn: props.list.sortColumn,
                sortOrderIsAsc: props.list.sortOrderIsAsc
            });
        } else {
            const prevDataId = prevProps.dataId ? prevProps.dataId : "";
            const dataId = props.dataId ? props.dataId : "";
            const prevCompanyGroupIds = prevProps.companyGroupIds ? prevProps.companyGroupIds.filter(i => i !== Base.emptyGuid) : [];
            const companyGroupIds = props.companyGroupIds ? props.companyGroupIds.filter(i => i !== Base.emptyGuid) : [];
            const prevActivityState = prevProps.activityState ? parseInt(prevProps.activityState, 10) : 0;
            const activityState = props.activityState ? parseInt(props.activityState, 10) : 0;
            const prevSortColumn = prevProps.sortColumn;
            const sortColumn = props.sortColumn;
            const prevSortOrderIsAsc = prevProps.sortOrderIsAsc;
            const sortOrderIsAsc = props.sortOrderIsAsc;
            if (dataId === prevDataId && Base.equalStrArrays(prevCompanyGroupIds, companyGroupIds) && prevActivityState === activityState &&
                prevSortColumn === sortColumn && prevSortOrderIsAsc === sortOrderIsAsc) return;
            this.searchItems(state.pageSize, state.page, state.filter, companyGroupIds, activityState, sortColumn, sortOrderIsAsc, true, false);
        }
    }

    changeFilter = (filter: string) => {
        const state = this.state;
        this.searchItems(state.pageSize, 1, filter, state.companyGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, true, false);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.searchItems(state.pageSize, !nextPage ? 1 : state.page + 1, state.filter, state.companyGroupIds, state.activityState, sortColumn, sortOrderIsAsc, !nextPage, false);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds,
            detailsItem: this.state.items.find(i => i.id === selectedId)
        });
    };
    
    handleListModeChange = (listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleListModeToggle = (_event: React.MouseEvent<HTMLElement>, listModeIsLine: boolean) => {
        this.setState({
            listModeIsLine,
        });
    };

    handleAdd = () => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, Base.emptyGuid), editDialogDefaultMenuItem: CompanyEditViewMode.General });
    };

    handleOpenLine = (id: string) => {
        this.setState({ ...List.getShowEditDialogStateChange(this.state, id), editDialogDefaultMenuItem: CompanyEditViewMode.General });
    };

    handleUserDialogOk = () => {
        this.setState({
            showEditDialog: false
        });
        this.refreshList();
    };

    handleUserDialogCancel = (listRefreshNeeded: boolean) => {
        this.setState({
            showEditDialog: false
        });
        if (!listRefreshNeeded) return;
        this.refreshList();
    };

    handleUserDialogNewSaved = (newCompanyId: string) => {
        this.setState({
            showEditDialog: true,
            editDialogId: newCompanyId,
            editDialogHasPrevious: false,
            editDialogHasNext: false,
            editDialogDefaultMenuItem: CompanyEditViewMode.Permissions
        });
        this.refreshList(false);
    };

    setEditDialogItemByIndex = (index: number) => {
        const state = this.state;
        this.setState(List.getEditDialogItemByIndexStateChange(this.state, index, () => this.searchItems(state.pageSize, state.page + 1, state.filter, state.companyGroupIds, state.activityState, state.sortColumn, state.sortOrderIsAsc, false, false, true)));
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (
            <>
                <PageTitle title={Translations.Companies + (props.savedFilter ? " - " + props.savedFilter.title : (props.listFilterIsSet || state.filter ? " - " + Translations.Filtered : ""))}>
                    <SettingsButton onClick={props.onToggleSettingsDialog} sx={{ marginRight: "1.5rem" }}>
                        {Translations.Settings}
                    </SettingsButton>
                    <AddButton variant="contained" onClick={this.handleAdd}>
                        {Translations.AddCompany}
                    </AddButton>
                </PageTitle>
                <MainListContainer>
                    <ActionContainer
                        margin="1rem .5rem 1rem 1rem"
                        leftItems={
                            props.reportTabs
                        }
                        rightItems={
                            <>
                                <ListActionButtons
                                    filterIsSet={props.listFilterIsSet}
                                    onFilter={props.onOpenFiltersDialog}
                                />
                                <ListToggleButtons value={state.listModeIsLine} onChange={this.handleListModeToggle} />
                            </>
                        }
                    />
                    <ListSearchContainer>
                        <ListSearchFilter
                            searchFilter={state.filter}
                            onSearchClick={this.changeFilter}
                        />
                    </ListSearchContainer>
                    <List
                        intialized={state.intialized}
                        isLoading={state.isLoading}
                        hasMore={state.hasMore}
                        globalLoadingInUse={!props.disableGlobalLoading}
                        height="calc(100vh - 296px)" //Main List
                        header={state.listModeIsLine
                            ? {
                                columns: [
                                    { size: 3, column: "code", content: Translations.Code },
                                    { size: 3, column: "name", content: Translations.CompanyName },
                                    { size: 2, column: "activeState", content: Translations.ActiveState },
                                    { size: 3, column: "groups", content: Translations.CompanyGroups },
                                    { size: 1, column: "usedFileStoragePercent", content: Translations.UsedQuota }
                                ]
                            }
                            : null}
                        lines={state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    columns: [
                                        {
                                            size: 3,
                                            column: "code",
                                            content:
                                            <Box display="flex" sx={{ overflow: "hidden" }}>
                                                <AvatarImage
                                                    name={item.name}
                                                    image={item.logo}
                                                    tooltip={item.name}
                                                    marginRight=".5rem"
                                                />
                                                <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                            </Box>
                                        },
                                        { size: 3, column: "name", content: item.name },
                                        { size: 2, column: "activeState", content: item.activeStateStr },
                                        { size: 3, column: "groups", content: item.companyGroupsStr },
                                        {
                                            size: 1,
                                            column: "usedFileStoragePercent",
                                            content: <>
                                                {item.fileStorageQuota > 0 &&
                                                <ProgressBar
                                                    text={item.usedStoragePercent.toLocaleFixed(0) + " % / " + item.usedFileStorageQuota.toLocaleFixed(2) + " GB"}
                                                    tooltip={String.format(Translations.ParameterPercentOfFileStorageQuotaUsed, item.usedStoragePercent)}
                                                    value={item.usedStoragePercent}
                                                    color={item.usedStoragePercent < 75.1 ? "info" : (item.usedStoragePercent < 90.1 ? "warning" : "error")}
                                                />
                                                }
                                            </>
                                        }
                                    ],
                                    id: item.id,
                                    classes: EnumHelper.isEqual(item.activeState, CompanyActiveState.NotInUse) ? "disabled" : ""
                                };
                            })
                            : null
                        }
                        cards={!state.listModeIsLine
                            ? state.items.map((item) => {
                                return {
                                    //classes: (EnumHelper.isEqual(item.activeState, CompanyActiveState.NotInUse) ? " disabled" : ""),
                                    sm: 4,
                                    rows: [
                                        {
                                            cols: [
                                                {
                                                    size: 6,
                                                    title: Translations.Code,
                                                    value:
                                                    <Box display="flex" sx={{ overflow: "hidden" }}>
                                                        <AvatarImage
                                                            name={item.name}
                                                            image={item.logo}
                                                            tooltip={item.name}
                                                            marginRight=".5rem"
                                                        />
                                                        <ListLinkValue onClick={() => this.handleOpenLine(item.id)}>{item.code}</ListLinkValue>
                                                    </Box>
                                                },
                                                { size: 6, title: Translations.CompanyName, value: item.name }
                                            ]
                                        },
                                        {
                                            cols: item.fileStorageQuota > 0
                                                ? [
                                                    { size: 6, title: Translations.ActiveState, value: item.activeStateStr },
                                                    {
                                                        size: 6,
                                                        title: Translations.UsedQuota,
                                                        value:
                                                            <ProgressBar
                                                                text={item.usedStoragePercent.toLocaleFixed(0) + " % / " + item.usedFileStorageQuota.toLocaleFixed(2) + " GB"}
                                                                tooltip={String.format(Translations.ParameterPercentOfFileStorageQuotaUsed, item.usedStoragePercent)}
                                                                value={item.usedStoragePercent}
                                                                color={item.usedStoragePercent < 75.1 ? "info" : (item.usedStoragePercent < 90.1 ? "warning" : "error")}
                                                            />
                                                    }
                                                ]
                                                : [{ title: Translations.ActiveState, value: item.activeStateStr }]
                                        },
                                        {
                                            classes: "row",
                                            cols: [
                                                { title: Translations.CompanyGroups, value: item.companyGroupsStr }
                                            ]
                                        }],
                                    id: item.id
                                };
                            })
                            : null
                        }
                        lineDetails={state.detailsItem
                            ? <Box position="relative">
                                <GridRow>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.Code} value={state.detailsItem.code} />
                                            </Grid>
                                        </GridRow>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.CompanyName} value={state.detailsItem.name} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.ActiveState} value={state.detailsItem.activeStateStr} />
                                            </Grid>
                                        </GridRow>
                                        {state.detailsItem.fileStorageQuota > 0 &&
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.UsedQuota} value={state.detailsItem.usedStoragePercent.toLocaleFixed(0) + " % / " + state.detailsItem.usedFileStorageQuota.toLocaleFixed(2) + " GB"} />
                                            </Grid>
                                        </GridRow>
                                        }
                                    </Grid>
                                    <Grid item sm={4}>
                                        <GridRow>
                                            <Grid item sm={12}>
                                                <ListCardField title={Translations.CompanyGroups} value={state.detailsItem.companyGroupsStr} />
                                            </Grid>
                                        </GridRow>
                                    </Grid>
                                </GridRow>
                                <ListDetailsButton
                                    onClick={() => this.handleOpenLine(state.selectedId)}
                                />
                            </Box>
                            : null}
                        sortColumn={state.sortColumn}
                        sortOrderIsAsc={state.sortOrderIsAsc}
                        selectedId={state.selectedId}
                        checkedIds={state.checkedIds}
                        onSetSelectedIds={this.handleSetSelectedIds}
                        onSearchLines={this.handleSearchLines}
                        onOpenLine={this.handleOpenLine}
                    />
                </MainListContainer>
                {(state.showEditDialog && !!state.editDialogId) &&
                    <CompanyEditDialog
                        sessionState={props.sessionState}
                        companyId={state.editDialogId}
                        defaultMenuItem={state.editDialogDefaultMenuItem}
                        hasPrevious={state.editDialogHasPrevious}
                        hasNext={state.editDialogHasNext}
                        onOk={this.handleUserDialogOk}
                        onCancel={this.handleUserDialogCancel}
                        onNewSaved={this.handleUserDialogNewSaved}
                        onRefreshList={() => this.refreshList(false)}
                        onPrevious={() => List.moveToPrevious(state, this.setEditDialogItemByIndex)}
                        onNext={() => List.moveToNext(state, this.setEditDialogItemByIndex)}
                        onRefreshSessionState={props.onRefreshSessionState}
                    />
                }
            </>
        );
    }
}